import React, { useEffect, useState, useMemo } from 'react';
import { Box, Tooltip, IconButton, CircularProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  Payments as PaymentsIcon,
  RecordVoiceOver as TranscricaoIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { colors } from '../theme/expertiseTheme';
import LogoImage from '../images/Expertise_Marca_FundosClaros_V1.png';
import { getStoredUser, clearAuthData, isAdmin as checkIsAdmin, hasToolAccess, Sector } from '../services/auth';

interface NavItem {
  icon: React.ReactNode;
  label: string;
  path: string;
  sector?: Sector | null;
  adminOnly?: boolean;
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Lê o user do localStorage e cria uma chave estável para evitar loops infinitos
  const user = useMemo(() => getStoredUser(), []);
  const isAdmin = useMemo(() => checkIsAdmin(user), [user]);
  
  // Cria uma string de dependência estável baseada nos dados do usuário
  // Isso evita que o useEffect seja executado quando o objeto user muda de referência
  const userKey = useMemo(() => {
    if (!user) return 'no-user';
    return JSON.stringify({
      id: user.id,
      email: user.email,
      roles: user.roles,
      sector: user.sector,
    });
  }, [user?.id, user?.email, user?.roles?.join(','), user?.sector]);

  useEffect(() => {
    // Montar itens de navegação baseados nas permissões
    const items: NavItem[] = [
      // Comuns - todos têm acesso
      { icon: <DashboardIcon />, label: 'Home', path: '/home', sector: null },
      { icon: <TranscricaoIcon />, label: 'Transcrição', path: '/transcricao', sector: null },
    ];

    // Financeiro - Admin ou Staff do Financeiro
    if (hasToolAccess(user, 'Financeiro')) {
      items.push({
        icon: <PaymentsIcon />,
        label: 'Pagamentos',
        path: '/financeiro/pagamentos',
        sector: 'Financeiro',
      });
    }

    // Outras ferramentas baseadas em setor (quando implementadas)
    if (hasToolAccess(user, 'Comercial')) {
      // items.push({ icon: <PeopleIcon />, label: 'Clientes', path: '/clientes', sector: 'Comercial' });
    }

    if (hasToolAccess(user, 'TI')) {
      // items.push({ icon: <TimelineIcon />, label: 'Analytics', path: '/analytics', sector: 'TI' });
    }

    // Configurações - todos têm acesso (mas admin vê mais opções)
    items.push({
      icon: <SettingsIcon />,
      label: 'Configurações',
      path: '/configuracoes',
      sector: null,
    });

    setNavItems(items);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    clearAuthData();
    navigate('/login', { replace: true });
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: 72,
          minHeight: '100vh',
          backgroundColor: colors.dark,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 1200,
        }}
      >
        <CircularProgress size={24} sx={{ color: colors.accent }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 72,
        minHeight: '100vh',
        backgroundColor: colors.dark,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 3,
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200,
      }}
    >
      {/* Logo */}
      {/* Navigation Items */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          flex: 1,
        }}
      >
        {navItems.map((item) => (
          <Tooltip key={item.path} title={item.label} placement="right" arrow>
            <IconButton
              onClick={() => navigate(item.path)}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                color: isActive(item.path) ? colors.accent : colors.text.onDarkSecondary,
                backgroundColor: isActive(item.path)
                  ? 'rgba(219, 250, 59, 0.1)'
                  : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(219, 250, 59, 0.1)',
                  color: colors.accent,
                },
              }}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      {/* User Info */}
      {user && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Tooltip
            title={`${user.email}\n${user.roles?.[0] || 'User'} - ${user.sector}`}
            placement="right"
            arrow
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                backgroundColor: isAdmin ? colors.primary : '#7C3AED',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.white,
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
              onClick={() => navigate('/configuracoes')}
            >
              {user.email?.charAt(0).toUpperCase() || 'U'}
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Logout Button */}
      <Tooltip title="Sair" placement="right" arrow>
        <IconButton
          onClick={handleLogout}
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            color: colors.text.onDarkSecondary,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(220, 38, 38, 0.2)',
              color: colors.error,
            },
          }}
        >
          <LogoutIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default Sidebar;
